import type { ServicePointCalibMetadata } from 'api/RobotApi'
import { ROBOT_MODES } from 'utils/constants'
import { LOT_TYPE } from 'utils/lotInterfaces'
import { ActionType, type ILotModelConfig } from 'utils/servicePointsCatalog'

import { actionTypeToRosCode, resolveSelectedCatalogActions } from './catalogItemActions'
import type { MaintenancePointKind, ServiceCalibCatalogData, ServiceCalibCatalogItem } from './serviceCalibTypes'
import {
  ACTION_CHARGING,
  ACTION_DEBRIS_DUMP,
  ACTION_PARKING,
  ACTION_REFILLING,
  ACTION_WASHING,
  ACTION_WASTE_WATER_DRAIN,
  KIND_MAINTENANCE_POINT,
  KIND_STATION_LOT,
} from './serviceCalibUtils'

/** Catalog service visit — OperatingMode 17 + metadata ref + action_type (MR-04e). */
export const CATALOG_SERVICE_MODE = ROBOT_MODES.MODE_SERVICE_POINT

export function isRobotCatalogServiceActive(
  activeMode: ROBOT_MODES | null | undefined,
  serverPending?: { mode: number } | null
): boolean {
  return activeMode === CATALOG_SERVICE_MODE || serverPending?.mode === CATALOG_SERVICE_MODE
}

const LOT_TYPE_TO_ACTION_ROS: Partial<Record<number, number>> = {
  [LOT_TYPE.PARKING]: ACTION_PARKING,
  [LOT_TYPE.WASHING]: ACTION_WASHING,
  [LOT_TYPE.CHARGING]: ACTION_CHARGING,
  [LOT_TYPE.REFILLING]: ACTION_REFILLING,
  [LOT_TYPE.DEBRIS_DUMP]: ACTION_DEBRIS_DUMP,
}

const MAINTENANCE_POINT_DEFAULT_ACTION_ROS: Partial<Record<MaintenancePointKind, number>> = {
  waste_water_drain: ACTION_WASTE_WATER_DRAIN,
  debris_dump: ACTION_DEBRIS_DUMP,
}

/** ROS action_type for catalog UI icons — first selected action or type default. */
export function getCatalogPointActionTypeFromItem(
  item: Pick<ServiceCalibCatalogItem, 'target' | 'lotType' | 'maintenancePointType'>,
  selectedActions?: ActionType[]
): number | null {
  const firstSelected = selectedActions?.[0]
  if (firstSelected != null) {
    return actionTypeToRosCode(firstSelected)
  }
  if (item.target === 'station_lot') {
    if (item.lotType == null) {
      return null
    }
    return LOT_TYPE_TO_ACTION_ROS[item.lotType] ?? null
  }
  if (!item.maintenancePointType) {
    return null
  }
  return MAINTENANCE_POINT_DEFAULT_ACTION_ROS[item.maintenancePointType] ?? null
}

/** Build OperatingMode.metadata for instant catalog service action (mode 17). */
export function buildCatalogServiceMetadata(
  item: ServiceCalibCatalogItem,
  lotModels: ILotModelConfig[],
  selectedByKey: Record<string, ActionType[]>,
  robotType?: string | null
): ServicePointCalibMetadata | null {
  const selectedActions = resolveSelectedCatalogActions(item, selectedByKey, lotModels, robotType)
  const actionCodes = selectedActions.map(actionTypeToRosCode).filter((code): code is number => code != null)
  if (!actionCodes.length) {
    return null
  }

  if (item.target === 'station_lot') {
    const lotModel = item.metadata.lot_model?.trim()
    if (!item.stationHardwareId || item.lotId == null || !lotModel) {
      return null
    }
    return {
      kind: KIND_STATION_LOT,
      action_type: actionCodes,
      station_id: item.stationHardwareId,
      lot_id: item.lotId,
      lot_model: lotModel,
    }
  }
  if (!item.maintenancePointId || !item.maintenancePointType) {
    return null
  }
  return {
    kind: KIND_MAINTENANCE_POINT,
    action_type: actionCodes,
    maintenance_point_id: item.maintenancePointId,
  }
}

export function supportsStationLotServiceAction(lotType?: number): boolean {
  switch (lotType) {
    case LOT_TYPE.CHARGING:
    case LOT_TYPE.WASHING:
    case LOT_TYPE.PARKING:
    case LOT_TYPE.REFILLING:
    case LOT_TYPE.DEBRIS_DUMP:
      return true
    default:
      return false
  }
}

export function supportsMaintenancePointServiceAction(type?: MaintenancePointKind): boolean {
  return type === 'waste_water_drain' || type === 'debris_dump' || type === 'universal'
}

export function isCatalogServiceRowActive(
  item: ServiceCalibCatalogItem,
  confirmedServiceKey: string | null | undefined
): boolean {
  return confirmedServiceKey === item.key
}

/** Row is starting catalog service (mode 17) — spinner while dispatch is in flight. */
export function isCatalogServiceRowActivating(
  item: ServiceCalibCatalogItem,
  serviceStartLoading: boolean
): boolean {
  return serviceStartLoading
}

export function findCatalogItemByServiceMetadata(
  catalog: ServiceCalibCatalogData,
  parsed: Record<string, unknown>
): ServiceCalibCatalogItem | null {
  const items = [...catalog.stationGroups.flatMap(group => group.lots), ...catalog.maintenancePoints]
  const kind = Number(parsed.kind)
  if (kind === KIND_STATION_LOT || parsed.target === 'station_lot') {
    const stationId = String(parsed.station_id ?? '')
    const lotId = Number(parsed.lot_id)
    return (
      items.find(
        item => item.target === 'station_lot' && item.stationHardwareId === stationId && item.lotId === lotId
      ) ?? null
    )
  }
  if (kind === KIND_MAINTENANCE_POINT || parsed.target === 'maintenance_point') {
    const maintenancePointId = Number(parsed.maintenance_point_id)
    return (
      items.find(item => item.target === 'maintenance_point' && item.maintenancePointId === maintenancePointId) ?? null
    )
  }
  return null
}

type ActiveModeMetadataWire = {
  kind?: number
  station_id?: string
  lot_id?: number
  lot_model?: string
  maintenance_point_id?: number
  action_type?: number[]
}

/** Catalog row in confirmed MODE_CALIBRATION_SERVICE_POINT (robot feedback, not pending). */
export function resolveConfirmedCatalogCalibKey(
  catalog: ServiceCalibCatalogData | null,
  activeMode: ROBOT_MODES | null | undefined,
  activeModeMetadata: ActiveModeMetadataWire | null | undefined
): string | null {
  if (activeMode !== ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT || !catalog || !activeModeMetadata) {
    return null
  }
  const item = findCatalogItemByServiceMetadata(catalog, activeModeMetadata as Record<string, unknown>)
  return item?.key ?? null
}

/** Catalog row in confirmed MODE_SERVICE_POINT (robot feedback, not pending). */
export function resolveConfirmedCatalogServiceKey(
  catalog: ServiceCalibCatalogData | null,
  activeMode: ROBOT_MODES | null | undefined,
  activeModeMetadata: ActiveModeMetadataWire | null | undefined
): string | null {
  if (activeMode !== CATALOG_SERVICE_MODE || !catalog || !activeModeMetadata) {
    return null
  }
  const item = findCatalogItemByServiceMetadata(catalog, activeModeMetadata as Record<string, unknown>)
  return item?.key ?? null
}

export function parseCatalogCalibItemKeyFromActionKey(actionKey: string | null | undefined): string | null {
  if (!actionKey) {
    return null
  }
  for (const prefix of ['catalog-calib-finish:', 'catalog-calib:']) {
    if (actionKey.startsWith(prefix)) {
      return actionKey.slice(prefix.length)
    }
  }
  return null
}

function getActionServiceLabel(action: ActionType, active = false): string | null {
  if (active) {
    return 'Stop'
  }
  switch (action) {
    case ActionType.CHARGING:
      return 'Charge'
    case ActionType.WASHING:
      return 'Wash'
    case ActionType.PARKING:
      return 'Park'
    case ActionType.REFILLING:
      return 'Refill'
    case ActionType.DEBRIS_DUMP:
      return 'Discharge'
    case ActionType.WASTE_WATER_DRAIN:
      return 'Drain'
    default:
      return null
  }
}

/** Short action label for catalog row service button. */
export function getStationLotServiceActionLabel(lotType?: number, active = false): string | null {
  if (active) {
    return 'Stop'
  }
  switch (lotType) {
    case LOT_TYPE.CHARGING:
      return 'Charge'
    case LOT_TYPE.WASHING:
      return 'Wash'
    case LOT_TYPE.PARKING:
      return 'Park'
    case LOT_TYPE.REFILLING:
      return 'Refill'
    case LOT_TYPE.DEBRIS_DUMP:
      return 'Discharge'
    default:
      return null
  }
}

export function getMaintenancePointServiceActionLabel(type?: MaintenancePointKind, active = false): string | null {
  if (active) {
    return 'Stop'
  }
  switch (type) {
    case 'waste_water_drain':
      return 'Drain'
    case 'debris_dump':
      return 'Discharge'
    case 'universal':
      return 'Service'
    default:
      return null
  }
}

export function getCatalogServiceActionLabel(
  item: ServiceCalibCatalogItem,
  active = false,
  selectedActions: ActionType[] = []
): string | null {
  if (active) {
    return 'Stop'
  }
  if (selectedActions.length === 1) {
    return getActionServiceLabel(selectedActions[0])
  }
  if (selectedActions.length > 1) {
    // Prefer concrete label when all selected actions share one family (e.g. only Charge).
    const unique = Array.from(new Set(selectedActions.map(a => getActionServiceLabel(a)).filter(Boolean)))
    if (unique.length === 1) {
      return unique[0]
    }
    return 'Service actions'
  }
  if (item.target === 'station_lot') {
    return getStationLotServiceActionLabel(item.lotType, active)
  }
  return getMaintenancePointServiceActionLabel(item.maintenancePointType, active)
}

export function supportsCatalogServiceAction(item: ServiceCalibCatalogItem): boolean {
  if (item.target === 'station_lot') {
    return supportsStationLotServiceAction(item.lotType)
  }
  return supportsMaintenancePointServiceAction(item.maintenancePointType)
}

/** Catalog service supported when robot publishes onboard_service_points[] (may be empty). */
export function isCatalogServiceModeAvailableOnRobot(onboardServicePoints: unknown[] | undefined | null): boolean {
  return Array.isArray(onboardServicePoints)
}
